import React, { useState, useEffect } from 'react';
import { fetchCategories } from '../services/api';
import { toast } from 'react-toastify';

const MiddleCategoryFormModal = ({ onClose, onSave, onBulkSave }) => {
  const [entryMode, setEntryMode] = useState('single'); // 'single' or 'bulk'
  const [formData, setFormData] = useState({
    name: '',
    categoryId: '',
  });
  const [bulkData, setBulkData] = useState({
    categoryId: '',
    middleCategories: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch categories when component mounts
  useEffect(() => {
    const loadCategories = async () => {
      setIsLoading(true);
      try {
        const data = await fetchCategories();
        setCategories(data);
      } catch (error) {
        toast.error('Failed to load categories');
        console.error('Error loading categories:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, []);

  const handleModeToggle = (mode) => {
    setEntryMode(mode);
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (entryMode === 'single') {
      setFormData({
        ...formData,
        [name]: value,
      });
    } else {
      setBulkData({
        ...bulkData,
        [name]: value,
      });
    }

    // Clear the error for this field when the user makes a change
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const validateSingleForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateBulkForm = () => {
    const newErrors = {};

    if (!bulkData.categoryId) {
      newErrors.categoryId = 'Category is required';
    }

    if (!bulkData.middleCategories.trim()) {
      newErrors.middleCategories = 'Middle categories list is required';
    } else {
      // Validate that middle categories are properly formatted
      const lines = bulkData.middleCategories.trim().split('\n');
      const validLines = lines.filter(line => line.trim());
      if (validLines.length === 0) {
        newErrors.middleCategories = 'At least one middle category name is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (entryMode === 'single') {
      if (!validateSingleForm()) {
        return;
      }
    } else {
      if (!validateBulkForm()) {
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (entryMode === 'single') {
        const singlePayload = {
          name: formData.name.trim(),
          categoryId: parseInt(formData.categoryId)
        };
        await onSave(singlePayload);
      } else {
        // Process bulk data
        const lines = bulkData.middleCategories.trim().split('\n');
        const middleCategoriesArray = lines
          .map(line => line.trim())
          .filter(line => line)
          .map(name => ({ name }));

        const bulkPayload = {
          categoryId: parseInt(bulkData.categoryId),
          middleCategories: middleCategoriesArray
        };

        await onBulkSave(bulkPayload);
      }
      onClose();
    } catch (error) {
      console.error('Error saving middle category:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            {entryMode === 'single' ? 'Add New Middle Category' : 'Bulk Add Middle Categories'}
          </h3>
          
          {/* Mode Toggle */}
          <div className="mt-3">
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
              <button
                type="button"
                onClick={() => handleModeToggle('single')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  entryMode === 'single'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Single Entry
              </button>
              <button
                type="button"
                onClick={() => handleModeToggle('bulk')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  entryMode === 'bulk'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Bulk Entry
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {/* Category Selection - Common for both modes */}
            <div>
              <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700">
                Category*
              </label>
              <select
                name="categoryId"
                id="categoryId"
                value={entryMode === 'single' ? formData.categoryId : bulkData.categoryId}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  errors.categoryId ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                disabled={isSubmitting || isLoading}
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {errors.categoryId && (
                <p className="mt-1 text-sm text-red-600">{errors.categoryId}</p>
              )}
            </div>

            {/* Single Entry Form */}
            {entryMode === 'single' && (
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Middle Category Name*
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                    errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                  placeholder="Enter middle category name"
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>
            )}

            {/* Bulk Entry Form */}
            {entryMode === 'bulk' && (
              <div>
                <label htmlFor="middleCategories" className="block text-sm font-medium text-gray-700">
                  Middle Categories*
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Enter one middle category name per line
                </p>
                <textarea
                  name="middleCategories"
                  id="middleCategories"
                  rows={8}
                  value={bulkData.middleCategories}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                    errors.middleCategories ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                  placeholder={`Enter middle category names, one per line:\n\nExample:\nWeb Development\nMobile Apps\nData Science\nAI/ML`}
                  disabled={isSubmitting}
                />
                {errors.middleCategories && (
                  <p className="mt-1 text-sm text-red-600">{errors.middleCategories}</p>
                )}
                
                {/* Preview */}
                {bulkData.middleCategories.trim() && (
                  <div className="mt-2 p-2 bg-gray-50 rounded-md">
                    <p className="text-xs font-medium text-gray-700 mb-1">Preview:</p>
                    <div className="text-xs text-gray-600">
                      {bulkData.middleCategories
                        .trim()
                        .split('\n')
                        .map(line => line.trim())
                        .filter(line => line)
                        .map((name, index) => (
                          <div key={index} className="truncate">• {name}</div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}


          </div>

          <div className="px-6 py-4 border-t flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : entryMode === 'single' ? 'Save' : 'Save All'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MiddleCategoryFormModal;
